import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  supabase,
  isSupabaseConfigured,
  isDemoModeEnabled,
} from "@/lib/supabase";
import {
  DEMO_USERS,
  readDemoSession,
  writeDemoSession,
  clearDemoSession,
} from "@/lib/demo";
import {
  clearNativePushOnSignOut,
  refreshNativePushRegistration,
} from "@/lib/nativePush";
import { ROLES } from "@/lib/roles";

const AuthContext = createContext(null);

const TENANT_ROLE_PRIORITY = [
  ROLES.ADMIN,
  ROLES.CMS_EDITOR,
  ROLES.TEACHER,
  ROLES.PARENT,
];

function selectTenantRole(assignments, organizationId) {
  const roles = new Set(
    (assignments || [])
      .filter((assignment) => assignment.organization_id === organizationId)
      .map((assignment) => assignment.role),
  );
  return TENANT_ROLE_PRIORITY.find((role) => roles.has(role)) || null;
}

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null); // { id, email, full_name, role, organization_id }
  const [loading, setLoading] = useState(true);
  const profileRequestRef = useRef(0);
  const activeOrganizationSwitchRef = useRef(null);
  const sessionUserIdRef = useRef(null);
  const authGenerationRef = useRef(0);

  // --- DEMO MODE (explicit local-development opt-in only) ---
  useEffect(() => {
    if (isSupabaseConfigured || !isDemoModeEnabled) {
      if (!isSupabaseConfigured) setLoading(false);
      return;
    }
    const existing = readDemoSession();
    if (existing) {
      setSession({ user: { id: existing.id, email: existing.email } });
      setProfile(existing);
    }
    setLoading(false);
  }, []);

  // --- LIVE MODE (Supabase configured) ---
  const loadProfile = useCallback(async (userId) => {
    if (!isSupabaseConfigured) return null;
    const { data, error } = await supabase
      .from("profiles")
      .select(
        "id, email, full_name, organization_id, role_assignments(organization_id, role)",
      )
      .eq("id", userId)
      .maybeSingle();
    if (error) {
      // eslint-disable-next-line no-console
      console.warn("[auth] profile fetch failed:", error.message);
      return null;
    }
    if (!data) return null;

    const assignments = data.role_assignments || [];
    const membershipIds = [...new Set(
      assignments.map((assignment) => assignment.organization_id).filter(Boolean),
    )];

    const [{ data: membershipOrganizations, error: orgError }, { data: platformRows, error: platformError }] =
      await Promise.all([
        membershipIds.length
          ? supabase
              .from("organizations")
              .select("id, name, display_name, slug")
              .in("id", membershipIds)
          : Promise.resolve({ data: [], error: null }),
        supabase
          .from("platform_memberships")
          .select("role, disabled_at")
          .eq("profile_id", data.id)
          .maybeSingle(),
      ]);

    // `platform_memberships` is introduced by the tenancy migration. Keep
    // older local/demo databases usable while production migrates first.
    if (orgError) {
      // eslint-disable-next-line no-console
      console.warn("[auth] organization membership lookup failed:", orgError.message);
    }
    if (platformError && platformError.code !== "42P01") {
      // eslint-disable-next-line no-console
      console.warn("[auth] platform role lookup failed:", platformError.message);
    }

    const orgById = new Map(
      (membershipOrganizations || []).map((organization) => [organization.id, organization]),
    );
    const memberships = membershipIds.map((organizationId) => ({
      organization_id: organizationId,
      organization: orgById.get(organizationId) || null,
      roles: assignments
        .filter((assignment) => assignment.organization_id === organizationId)
        .map((assignment) => assignment.role),
    }));
    const tenantRole = selectTenantRole(assignments, data.organization_id);
    const platformRole = platformRows?.disabled_at ? null : platformRows?.role || null;
    // Platform access deliberately takes precedence over a tenant workspace.
    // This makes it impossible to accidentally treat platform membership as a
    // family-data role in the client router.
    const role = platformRole ? ROLES.PLATFORM_ADMIN : tenantRole;
    return {
      id: data.id,
      email: data.email,
      full_name: data.full_name,
      organization_id: data.organization_id,
      role,
      tenant_role: tenantRole,
      platform_role: platformRole,
      memberships,
    };
  }, []);

  // Profile hydration with its own visible state. A sign-in is only
  // "done" once the profile (and role) is loaded; tracking this
  // separately lets the router show a spinner while it's in flight and
  // a retry screen if it fails — never an infinite spinner (the exact
  // failure Google flagged in review on slow devices).
  const [profileLoading, setProfileLoading] = useState(false);

  const withTimeout = (promise, ms) =>
    Promise.race([
      promise,
      new Promise((_, reject) =>
        setTimeout(() => reject(new Error("profile fetch timed out")), ms),
      ),
    ]);

  const refreshProfile = useCallback(
    async (userId, { transitionToken = null } = {}) => {
      if (!userId) return null;
      const switchMatches = () => (
        transitionToken === null
          ? activeOrganizationSwitchRef.current === null
          : activeOrganizationSwitchRef.current === transitionToken
      );
      if (!switchMatches()) return null;

      const requestId = ++profileRequestRef.current;
      const canCommit = () => (
        requestId === profileRequestRef.current &&
        sessionUserIdRef.current === userId &&
        switchMatches()
      );
      setProfileLoading(true);
      try {
        let p = null;
        try {
          p = await withTimeout(loadProfile(userId), 8000);
        } catch {
          // One quiet retry — transient mobile-network stalls are common.
          p = await withTimeout(loadProfile(userId), 8000).catch(() => null);
        }
        if (!canCommit()) return null;
        setProfile(p);
        return p;
      } finally {
        if (canCommit()) setProfileLoading(false);
      }
    },
    [loadProfile],
  );

  useEffect(() => {
    if (!isSupabaseConfigured) return;
    let mounted = true;
    const initialAuthGeneration = authGenerationRef.current;

    // Watchdog: if something in the auth SDK hangs (e.g. a blocked token
    // refresh call that never resolves because of a proxy layer), don't
    // leave the UI stuck on a spinner forever.
    const watchdog = setTimeout(() => {
      if (mounted) {
        // eslint-disable-next-line no-console
        console.warn("[auth] getSession watchdog fired; releasing loading gate");
        setLoading(false);
      }
    }, 4000);

    supabase.auth
      .getSession()
      .then(async ({ data }) => {
        // The initial session lookup has completed. Clear the auth watchdog
        // before profile hydration so a slow profile request is represented
        // by the profile loading state instead of a misleading auth warning.
        clearTimeout(watchdog);
        if (!mounted || initialAuthGeneration !== authGenerationRef.current) return;
        const nextSession = data.session;
        const userId = nextSession?.user?.id || null;
        sessionUserIdRef.current = userId;
        setSession(nextSession);
        if (userId) {
          const hydrated = await refreshProfile(userId);
          // Refresh an already-enabled native token after a successful
          // session hydration. This never asks permission or enables a new
          // device, but it lets APNs/FCM rotate a prior registration safely.
          if (
            hydrated &&
            mounted &&
            initialAuthGeneration === authGenerationRef.current
          ) void refreshNativePushRegistration().catch(() => {});
        }
        if (mounted && initialAuthGeneration === authGenerationRef.current) {
          setLoading(false);
        }
      })
      .catch((err) => {
        clearTimeout(watchdog);
        // eslint-disable-next-line no-console
        console.warn("[auth] getSession failed:", err?.message || err);
        if (mounted && initialAuthGeneration === authGenerationRef.current) {
          setLoading(false);
        }
      });

    const { data: sub } = supabase.auth.onAuthStateChange(
      (_event, nextSession) => {
        const authGeneration = ++authGenerationRef.current;
        const previousUserId = sessionUserIdRef.current;
        const userId = nextSession?.user?.id || null;
        const sameUserSwitchInFlight = Boolean(
          userId &&
          previousUserId === userId &&
          activeOrganizationSwitchRef.current !== null
        );
        sessionUserIdRef.current = userId;
        setSession(nextSession);
        setLoading(false);

        if (sameUserSwitchInFlight) return;

        activeOrganizationSwitchRef.current = null;
        profileRequestRef.current += 1;
        if (userId) {
          if (previousUserId !== userId) setProfile(null);
          setProfileLoading(true);
          // Never `await` supabase calls inside this callback — the SDK
          // holds its auth lock while dispatching and can deadlock
          // (notorious in Android WebViews). Defer to the next tick.
          setTimeout(() => {
            if (authGeneration !== authGenerationRef.current) return;
            void refreshProfile(userId).then((hydrated) => {
              if (hydrated && authGeneration === authGenerationRef.current) {
                void refreshNativePushRegistration().catch(() => {});
              }
            });
          }, 0);
        } else {
          setProfile(null);
          setProfileLoading(false);
        }
      },
    );

    return () => {
      mounted = false;
      clearTimeout(watchdog);
      sub?.subscription?.unsubscribe();
    };
  }, [refreshProfile]);

  const signIn = useCallback(async (email, password) => {
    if (!isSupabaseConfigured) {
      throw new Error("Live authentication is not configured.");
    }
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (error) throw error;
  }, []);

  const signUp = useCallback(async (email, password, fullName) => {
    if (!isSupabaseConfigured) {
      throw new Error("Supabase not configured.");
    }
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: { data: { full_name: fullName } },
    });
    if (error) throw error;
  }, []);

  const resetPasswordForEmail = useCallback(async (email) => {
    if (!isSupabaseConfigured) {
      throw new Error("Password recovery requires live Supabase authentication.");
    }
    const redirectTo =
      typeof window !== "undefined"
        ? `${window.location.origin}/reset-password`
        : undefined;
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo,
    });
    if (error) throw error;
  }, []);

  const updatePassword = useCallback(async (password) => {
    if (!isSupabaseConfigured) {
      throw new Error("Password updates require live Supabase authentication.");
    }
    const { error } = await supabase.auth.updateUser({ password });
    if (error) throw error;
  }, []);

  const signOut = useCallback(async () => {
    if (isSupabaseConfigured) {
      // Best-effort and bounded: native device cleanup needs the current
      // caller JWT, so attempt it before signing out without making a slow or
      // offline notification provider block account exit.
      await Promise.race([
        clearNativePushOnSignOut(),
        new Promise((resolve) => setTimeout(resolve, 2000)),
      ]);
      await supabase.auth.signOut();
    } else {
      clearDemoSession();
      setSession(null);
      setProfile(null);
    }
  }, []);

  const setActiveOrganization = useCallback(
    async (organizationId) => {
      if (!isSupabaseConfigured) {
        throw new Error("Center switching requires live Supabase authentication.");
      }
      if (!profile?.id) throw new Error("Sign in to switch centers.");
      const allowed = profile.memberships?.some(
        (membership) => membership.organization_id === organizationId,
      );
      if (!allowed) {
        throw new Error("You do not belong to that center.");
      }
      if (organizationId === profile.organization_id) return profile;

      const previousProfile = profile;
      const transitionToken = Symbol("active-organization-switch");
      activeOrganizationSwitchRef.current = transitionToken;
      profileRequestRef.current += 1;
      // Unmount the tenant workspace before the server-side selector changes.
      // Keeping the former center rendered while profile hydration is in
      // flight can let an authorized response from the new center populate an
      // old-center component state.
      setProfileLoading(true);
      setProfile(null);
      let serverOrganizationUpdated = false;
      try {
        const { error } = await supabase
          .from("profiles")
          .update({ organization_id: organizationId })
          .eq("id", previousProfile.id);
        if (error) throw error;
        serverOrganizationUpdated = true;
        const refreshedProfile = await refreshProfile(previousProfile.id, {
          transitionToken,
        });
        if (!refreshedProfile) {
          throw new Error("Selected center could not complete profile hydration.");
        }
        // Native delivery is intentionally scoped to the active center. This
        // bounded refresh keeps an opted-in device associated with the center
        // the member just selected without prompting or changing preferences.
        void refreshNativePushRegistration().catch(() => {});
        return refreshedProfile;
      } catch (error) {
        // Restore the prior profile only when the server rejected the update.
        // Once the server selector changes, leaving profile null routes the
        // user through the retry state instead of presenting a stale center.
        if (
          !serverOrganizationUpdated &&
          activeOrganizationSwitchRef.current === transitionToken &&
          sessionUserIdRef.current === previousProfile.id
        ) setProfile(previousProfile);
        throw error;
      } finally {
        if (activeOrganizationSwitchRef.current === transitionToken) {
          activeOrganizationSwitchRef.current = null;
          setProfileLoading(false);
        }
      }
    },
    [profile, refreshProfile],
  );

  const demoSignIn = useCallback((role) => {
    if (!isDemoModeEnabled) return;
    const user = DEMO_USERS[role] || DEMO_USERS[ROLES.PARENT];
    writeDemoSession(user);
    setSession({ user: { id: user.id, email: user.email } });
    setProfile(user);
  }, []);

  const value = useMemo(
    () => ({
      session,
      profile,
      loading,
      profileLoading,
      retryProfile: () => refreshProfile(session?.user?.id),
      role: profile?.role || null,
      tenantRole: profile?.tenant_role || null,
      platformRole: profile?.platform_role || null,
      memberships: profile?.memberships || [],
      isAuthenticated: Boolean(session && profile),
      signIn,
      signUp,
      resetPasswordForEmail,
      updatePassword,
      signOut,
      setActiveOrganization,
      demoSignIn,
      isSupabaseConfigured,
      isDemoModeEnabled,
    }),
    [session, profile, loading, profileLoading, refreshProfile, signIn, signUp, resetPasswordForEmail, updatePassword, signOut, setActiveOrganization, demoSignIn],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
